import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, FileUp, Loader2, Plus, Stethoscope } from 'lucide-react';
import { toast } from 'sonner';
import type {
  LeaveRegisterRow, SickLeaveCareFor, SickLeaveLetterCode, SickLeaveListItem,
} from '@ip/shared-types';
import { SICK_LEAVE_LETTER_CODES } from '@ip/shared-types';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { DatePicker } from '@/components/shared/DatePicker';
import { useHrApi, SessionExpiredError } from '@/services/api';
import { formatDateRange, formatDays, parseYmd, toYmd } from '@/lib/hr';
import { t } from '@/i18n';
import { PueImportWizard } from './PueImportWizard';

interface SickLeavePanelProps {
  nip: string;
  /** Employee options for the manual-entry dialog (from the leave register). */
  employees: Array<Pick<LeaveRegisterRow, 'employeeId' | 'firstName' | 'lastName'>>;
}

/**
 * The e-ZLA register (hr/admin only — the letter code A–E is health-adjacent
 * data managers never see) + manual certificate entry + the PUE import
 * wizard. There is no employer API at ZUS: delivery is import or typing.
 */
export function SickLeavePanel({ nip, employees }: SickLeavePanelProps) {
  const { sickLeaves, createSickLeave } = useHrApi();
  const year = new Date().getFullYear();
  const [rows, setRows] = useState<SickLeaveListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await sickLeaves(nip, `${year}-01-01`, `${year}-12-31`));
    } catch (error) {
      if (!(error instanceof SessionExpiredError)) toast.error(t('hr.common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [sickLeaves, nip, year]);

  useEffect(() => { load(); }, [load]);

  const careLabel = (careFor: SickLeaveCareFor | null) =>
    careFor === 'child' ? t('hr.sick.careChild') : careFor === 'family' ? t('hr.sick.careFamily') : t('hr.sick.careSelf');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-muted-foreground text-sm">{t('hr.sick.title')} · {year}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
            <FileUp className="size-4" />
            {t('hr.sick.import')}
          </Button>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="size-4" />
            {t('hr.sick.add')}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-center text-sm">
          <Stethoscope className="size-8 opacity-40" />
          <p>{t('hr.sick.empty')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('hr.sick.employee')}</TableHead>
                <TableHead>{t('hr.sick.period')}</TableHead>
                <TableHead className="text-right">{t('hr.sick.days')}</TableHead>
                <TableHead>{t('hr.sick.code')}</TableHead>
                <TableHead>{t('hr.sick.careForCol')}</TableHead>
                <TableHead>{t('hr.sick.series')}</TableHead>
                <TableHead>{t('hr.sick.source')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="whitespace-nowrap font-medium">{row.employeeName}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatDateRange(row.dateFrom, row.dateTo)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatDays(row.days)}</TableCell>
                  <TableCell>
                    {row.letterCode ? (
                      <Badge variant="outline" className="border-rose-500/40 bg-rose-500/10 font-mono text-rose-700 dark:text-rose-400">
                        {row.letterCode}
                      </Badge>
                    ) : '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{careLabel(row.careFor)}</TableCell>
                  <TableCell className="text-muted-foreground font-mono text-xs">{row.seriesNumber ?? '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-normal">
                      {row.source === 'pue_import' ? t('hr.sick.sourcePue') : t('hr.sick.sourceManual')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddSickLeaveDialog
        nip={nip}
        open={addOpen}
        onOpenChange={setAddOpen}
        employees={employees}
        onCreated={load}
        createSickLeave={createSickLeave}
      />
      <PueImportWizard nip={nip} open={importOpen} onOpenChange={setImportOpen} onImported={load} />
    </div>
  );
}

function AddSickLeaveDialog({
  nip, open, onOpenChange, employees, onCreated, createSickLeave,
}: {
  nip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employees: SickLeavePanelProps['employees'];
  onCreated: () => void;
  createSickLeave: ReturnType<typeof useHrApi>['createSickLeave'];
}) {
  const [employeeId, setEmployeeId] = useState('');
  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [letterCode, setLetterCode] = useState<'none' | SickLeaveLetterCode>('none');
  const [careFor, setCareFor] = useState<SickLeaveCareFor>('self');
  const [series, setSeries] = useState('');
  const [override, setOverride] = useState(false);
  const [conflictMsg, setConflictMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmployeeId('');
    setFrom(null);
    setTo(null);
    setLetterCode('none');
    setCareFor('self');
    setSeries('');
    setOverride(false);
    setConflictMsg(null);
    setError(null);
    setBusy(false);
  }, [open]);

  const sorted = useMemo(
    () => [...employees].sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'pl')),
    [employees],
  );

  const pickerToYmd = (d: Date | null) =>
    d ? toYmd(new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))) : null;

  const canSave = !!employeeId && !!from && !!to && from <= to && !busy;

  const save = async () => {
    if (!canSave || !from || !to) return;
    setBusy(true);
    setError(null);
    try {
      const res = await createSickLeave(
        nip,
        {
          employeeId,
          dateFrom: from,
          dateTo: to,
          seriesNumber: series.trim() || undefined,
          letterCode: letterCode === 'none' ? undefined : letterCode,
          careFor,
        },
        override,
      );
      toast.success(t('hr.sick.created'), {
        description: res.vacationSplits.length > 0
          ? t('hr.sick.splitsInfo', { count: res.vacationSplits.length })
          : undefined,
      });
      onCreated();
      onOpenChange(false);
    } catch (e) {
      if (e instanceof SessionExpiredError) return;
      const message = e instanceof Error ? e.message : String(e);
      // 409 overlap → offer the art. 166 override instead of a dead end.
      if (/overlap|art\. ?166|vacation/i.test(message) && !override) {
        setConflictMsg(message);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('hr.sick.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('hr.sick.dialogBody')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>{t('hr.sick.employee')}</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('hr.sick.employeePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {sorted.map((e) => (
                  <SelectItem key={e.employeeId} value={e.employeeId}>
                    {e.lastName} {e.firstName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('hr.request.dateFrom')}</Label>
              <DatePicker
                value={from ? parseYmd(from) : null}
                onChange={(d) => {
                  const v = pickerToYmd(d);
                  setFrom(v);
                  if (v && (!to || to < v)) setTo(v);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('hr.request.dateTo')}</Label>
              <DatePicker
                value={to ? parseYmd(to) : null}
                onChange={(d) => setTo(pickerToYmd(d))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('hr.sick.code')} <span className="text-muted-foreground">({t('hr.sick.optional')})</span></Label>
              <Select value={letterCode} onValueChange={(v) => setLetterCode(v as 'none' | SickLeaveLetterCode)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('hr.sick.codeNone')}</SelectItem>
                  {SICK_LEAVE_LETTER_CODES.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code} — {t(`hr.sick.codeHint.${code}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('hr.sick.careForCol')}</Label>
              <Select value={careFor} onValueChange={(v) => setCareFor(v as SickLeaveCareFor)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">{t('hr.sick.careSelf')}</SelectItem>
                  <SelectItem value="child">{t('hr.sick.careChild')}</SelectItem>
                  <SelectItem value="family">{t('hr.sick.careFamily')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="sl-series">
              {t('hr.sick.series')} <span className="text-muted-foreground">({t('hr.sick.optional')})</span>
            </Label>
            <Input id="sl-series" value={series} onChange={(e) => setSeries(e.target.value)} placeholder="ZZ 1234567" />
          </div>

          {conflictMsg && (
            <Alert className="border-amber-500/50">
              <AlertTriangle className="size-4 text-amber-600" />
              <AlertDescription className="space-y-2 text-amber-700 dark:text-amber-400">
                <p>{conflictMsg}</p>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Checkbox checked={override} onCheckedChange={(v) => setOverride(v === true)} />
                  {t('hr.sick.overrideLabel')}
                </label>
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
            {t('hr.request.cancel')}
          </Button>
          <Button disabled={!canSave || (!!conflictMsg && !override)} onClick={save}>
            {busy && <Loader2 className="size-4 animate-spin" />}
            {t('hr.sick.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
